import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@material-ui/lab';
import {
  useUserClassroomMoves,
  userClassroomMovesGenerate,
  userClassroomMovesWarmup
} from 'sdk';

import { ReactComponent as FocusIcon } from 'pages/Teachers/Home/assets/focus-icon.svg';
import { ReactComponent as RevisitIcon } from 'pages/Teachers/Home/assets/revisit-icon.svg';
import StartEmptyBox from 'pages/Teachers/Home/components/StartEmptyBox';

import styles from './styles.module.scss';

const TomorrowsFocus = () => {
  // const [isFailed, setIsFailed] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [, setTick] = useState(0); // state to trigger re-render every minute

  const [startPolling, setStartPolling] = useState(false);

  const {
    data: userClassroomMovesData,
    isLoading: isUserClassroomMovesLoading,
    refetch: refetchUserClassroomMoves
  } = useUserClassroomMoves();

  const tenMinutesAfterLastFetch = useMemo(() => {
    const lastFetchTime = userClassroomMovesData?.created_at;
    if (isUserClassroomMovesLoading) return false;
    if (!lastFetchTime) return true;

    const tenMinutes = 12 * 60 * 1000;
    return (
      new Date().getTime() - new Date(lastFetchTime).getTime() > tenMinutes
    );
  }, [userClassroomMovesData, isUserClassroomMovesLoading]);

  const fetchFocusData = useCallback(async () => {
    setIsLoading(true);
    try {
      await userClassroomMovesGenerate();
      refetchUserClassroomMoves();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [refetchUserClassroomMoves]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1); // trigger re-render
    }, 60000); // update every minute

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (startPolling) {
      const interval = setInterval(() => {
        refetchUserClassroomMoves();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [startPolling, refetchUserClassroomMoves]);

  useEffect(() => {
    if (
      tenMinutesAfterLastFetch === true &&
      userClassroomMovesData?.generation_in_progress === false
    ) {
      fetchFocusData();
    }
  }, [
    tenMinutesAfterLastFetch,
    userClassroomMovesData?.generation_in_progress,
    fetchFocusData
  ]);

  useEffect(() => {
    userClassroomMovesWarmup();
  }, []);

  useEffect(() => {
    if (userClassroomMovesData?.generation_in_progress === true) {
      setStartPolling(true);
    } else {
      setStartPolling(false);
    }
  }, [userClassroomMovesData?.generation_in_progress]);

  return (
    <div className={styles.focusWidget}>
      {(isLoading || isUserClassroomMovesLoading) && (
        <div className={styles.loadingState}>
          <Skeleton variant="rect" width="100%" height={180} />
        </div>
      )}
      {!isLoading &&
        !isUserClassroomMovesLoading &&
        userClassroomMovesData?.suggested_moves.length > 0 && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <RevisitIcon className={styles.warningIcon} />
              <span className={styles.headerTitle}>Revisit</span>
            </div>

            <ul className={styles.bulletList}>
              {userClassroomMovesData?.suggested_moves.map((bullet, index) => (
                <li key={index} className={styles.bulletItem}>
                  {bullet}
                </li>
              ))}
            </ul>
          </div>
        )}
      {!isLoading &&
        !isUserClassroomMovesLoading &&
        userClassroomMovesData.length === 0 && (
          <StartEmptyBox
            stepNumber="STEP 2"
            description="Reflect on today's data and set goals for your next lesson."
            icon={<FocusIcon />}
          />
        )}
    </div>
  );
};

export default TomorrowsFocus;
